/**
 * Display formatting helpers for time entries (hours, dates, ticket labels).
 */

import type { StandardTimeEntry } from "./types";

/**
 * Format hours for display
 */
export function formatHours(hours: number | undefined | null): string {
  if (hours === undefined || hours === null) {
    return "0.00h";
  }
  return `${Number(hours).toFixed(2)}h`;
}

/**
 * Format date for display
 */
export function formatDate(dateString: string | undefined | null): string {
  if (!dateString) {
    return "Unknown Date";
  }

  // Extract just the date part (YYYY-MM-DD) from various formats
  // Handles: "2025-10-21", "2025-10-21T00:00:00Z", "2025-10-21T00:00:00-06:00"
  const dateMatch = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);

  if (!dateMatch) {
    return "Invalid Date";
  }

  const [, yearStr, monthStr, dayStr] = dateMatch;
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);

  // Parse DATE strings as local dates, not UTC
  // "2025-10-21" should be treated as 2025-10-21 in user's timezone
  // This prevents the timezone offset issue where UTC parsing causes
  // dates to display one day earlier for users not in UTC timezone
  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Get display name for ticket
 */
export function getTicketDisplayName(entry: StandardTimeEntry): string {
  if (entry.ticket_number) {
    return entry.ticket_title
      ? `${entry.ticket_number} - ${entry.ticket_title}`
      : entry.ticket_number;
  }
  return "Unknown Ticket";
}
